import { HtsReadinessPillarKey } from "../constants";
import { HtsReadinessPillarConfig } from "../schemas";

/**
 * Fitness's dimension keys. Named rather than inlined because
 * htsReadinessScoring.ts reads `DemonstratedTestReadiness` back by key to
 * apply the documented-pass pillar override — a bare literal there would
 * drift silently against this config.
 */
export const HtsFitnessDimensionKey = {
  FitnessHabitConsistency: "fitnessHabitConsistency",
  TestSpecificPreparation: "testSpecificPreparation",
  DemonstratedTestReadiness: "demonstratedTestReadiness",
} as const;
export type HtsFitnessDimensionKey = (typeof HtsFitnessDimensionKey)[keyof typeof HtsFitnessDimensionKey];

export const fitness: HtsReadinessPillarConfig = {
  key: HtsReadinessPillarKey.FITNESS,
  label: "Fitness",
  definition:
    "Personal fitness plus preparation specific to the required physical abilities test for the candidate's target role. Recency rules: test-specific training counts within the last 3 months; an official administered pass within the last 6 months.",
  dataSourceNote: "Asked — current routine; test-specific training and recency; practice run or official test and result.",
  dimensions: [
    {
      key: HtsFitnessDimensionKey.FitnessHabitConsistency,
      label: "Fitness Habit & Consistency",
      weight: 0.3,
      anchors: {
        0: "No fitness regime and no regular physical activity.",
        25: "Sporadic activity with no routine (occasional gym visits, seasonal sport only).",
        50: "Semi-regular aerobic activity or recreational sport, roughly weekly.",
        75: "Regular fitness regime or aerobic team sport, multiple sessions per week, sustained for months.",
        100: "Structured regular regime plus aerobic sport, sustained 6+ months, with progression the candidate can describe.",
      },
    },
    {
      key: HtsFitnessDimensionKey.TestSpecificPreparation,
      label: "Test-Specific Preparation",
      weight: 0.4,
      anchors: {
        0: "No test-specific training and no knowledge of the test's components.",
        25: "Knows the test's components; has done no training targeting them. General exercise that isn't one of the test's components (walking, push-ups, sit-ups) is not test-specific training, even when done to prepare for the test.",
        50: "Has done test-specific training, but stale — outside the recency window (3 months, or the agency's stated window if shorter).",
        75: "Recent (within the recency window) test-specific training on the actual components or close simulations.",
        100: "Recent, structured program built around the specific test, with measured progress the candidate can cite.",
      },
    },
    {
      key: HtsFitnessDimensionKey.DemonstratedTestReadiness,
      label: "Demonstrated Test Readiness",
      weight: 0.3,
      anchors: {
        0: "Never attempted a practice run or test.",
        25: "Attempted a practice run and did not pass.",
        50: "Passed a practice run at or near the pass standard (time, or all stations on a pass/fail test).",
        75: "Passed a practice run with a clear or comfortable margin (more than 5% under the pass time, or all stations passed with reserve), but not yet an officially administered test.",
        100: "Passed an officially administered test within the validity window (6 months, or the agency's stated window if shorter), documented.",
      },
    },
  ],
  topIndicators: [
    "A recent timed practice pass with a comfortable margin",
    "A routine the candidate describes in specifics rather than adjectives",
  ],
  scoringNotes: [
    "A routine that wouldn't prepare someone for the role's physical test scores no higher than 25 on Fitness Habit & Consistency.",
    "No document upload exists in this questionnaire — 'documented' means the answer itself is specific enough to substantiate the claim, not a bare assertion. A claimed official pass with no such specifics caps Demonstrated Test Readiness at 75, noted for the coach.",
    "Accommodation and medical status are not asked and not scored; this pillar does not screen any candidate out.",
  ],
};
